import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { lessonService } from '../services/lesson-service';
import { handleApiError } from '../utils/api-helpers';
const initialState = {
    items: [],
    current_lesson: null,
    is_loading: false,
    error: null,
};
// Async thunk for creating a lesson
export const create_lesson = createAsyncThunk('lessons/create_lesson', async ({ lessonData, file }, { rejectWithValue }) => {
    try {
        return await lessonService.createLesson(lessonData);
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for fetching all lessons
export const fetch_all_lessons = createAsyncThunk('lessons/fetch_all_lessons', async (_, { rejectWithValue }) => {
    try {
        return await lessonService.getAllLessons();
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for fetching lessons by tutor ID
export const fetch_lessons_by_tutor_id = createAsyncThunk('lessons/fetch_lessons_by_tutor_id', async ({ tutorId, useTestEndpoint = false }, { rejectWithValue }) => {
    try {
        return await lessonService.getLessonsByTutor(tutorId);
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for fetching a single lesson.
// Accepts a bare lessonId (default RLS path) or, for in-meeting followers, an
// object carrying a viewing token that unlocks a tutor-shared lesson the
// student doesn't own.
export const fetch_lesson = createAsyncThunk('lessons/fetch_lesson', async (arg, { rejectWithValue }) => {
    try {
        const lessonId = typeof arg === 'string' ? arg : arg.lessonId;
        const meetingToken = typeof arg === 'string' ? null : arg.meetingLessonToken ?? null;
        const lesson = meetingToken
            ? await lessonService.getLessonByMeetingToken(lessonId, meetingToken)
            : await lessonService.getLessonById(lessonId);
        if (!lesson) {
            return null;
        }
        return lesson;
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for updating a lesson
export const update_lesson = createAsyncThunk('lessons/update_lesson', async ({ lessonId, lessonData, file }, { rejectWithValue }) => {
    try {
        return await lessonService.updateLesson(lessonId, lessonData);
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
// Async thunk for deleting a lesson
export const delete_lesson = createAsyncThunk('lessons/delete_lesson', async (lessonId, { rejectWithValue }) => {
    try {
        const result = await lessonService.deleteLesson(lessonId);
        return {
            lessonId,
            deletedFiles: result.deletedFiles,
            message: result.message
        };
    }
    catch (error) {
        return rejectWithValue(handleApiError(error));
    }
});
const lessonSlice = createSlice({
    name: 'lessons',
    initialState,
    reducers: {
        clear_error: (state) => {
            state.error = null;
        },
        clear_current_lesson: (state) => {
            state.current_lesson = null;
        },
        set_current_lesson: (state, action) => {
            state.current_lesson = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(create_lesson.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(create_lesson.fulfilled, (state, action) => {
            state.is_loading = false;
            if (action.payload) {
                state.items.push(action.payload);
                state.current_lesson = action.payload;
            }
            else {
                console.error('❌ Redux: create_lesson.fulfilled received null payload');
            }
        })
            .addCase(create_lesson.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'Failed to create lesson';
        })
            .addCase(fetch_all_lessons.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetch_all_lessons.fulfilled, (state, action) => {
            state.is_loading = false;
            state.items = action.payload;
        })
            .addCase(fetch_all_lessons.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'Failed to fetch lessons';
        })
            .addCase(fetch_lessons_by_tutor_id.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetch_lessons_by_tutor_id.fulfilled, (state, action) => {
            state.is_loading = false;
            state.items = action.payload;
        })
            .addCase(fetch_lessons_by_tutor_id.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'Failed to fetch tutor lessons';
        })
            .addCase(fetch_lesson.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(fetch_lesson.fulfilled, (state, action) => {
            state.is_loading = false;
            state.current_lesson = action.payload;
            if (action.payload && action.payload.id) {
                const index = state.items.findIndex(item => item.id === action.payload?.id);
                if (index !== -1) {
                    state.items[index] = action.payload;
                }
            }
            else if (action.payload !== null) {
                console.error('❌ Redux: fetch_lesson.fulfilled received invalid payload:', action.payload);
            }
        })
            .addCase(fetch_lesson.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'Failed to fetch lesson';
        })
            .addCase(update_lesson.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(update_lesson.fulfilled, (state, action) => {
            state.is_loading = false;
            state.current_lesson = action.payload;
            if (action.payload && action.payload.id) {
                const index = state.items.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.items[index] = action.payload;
                }
            }
            else {
                console.error('❌ Redux: update_lesson.fulfilled received invalid payload:', action.payload);
            }
        })
            .addCase(update_lesson.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'Failed to update lesson';
        })
            .addCase(delete_lesson.pending, (state) => {
            state.is_loading = true;
            state.error = null;
        })
            .addCase(delete_lesson.fulfilled, (state, action) => {
            state.is_loading = false;
            state.items = state.items.filter(item => item.id !== action.payload.lessonId);
            if (state.current_lesson?.id === action.payload.lessonId) {
                state.current_lesson = null;
            }
        })
            .addCase(delete_lesson.rejected, (state, action) => {
            state.is_loading = false;
            state.error = action.payload || 'Failed to delete lesson';
        });
    },
});
export const { clear_error, clear_current_lesson, set_current_lesson } = lessonSlice.actions;
export default lessonSlice.reducer;
